import { useState, useEffect } from "react";
//MUI
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import ModalStockFirstStep from "./ModalStockFirstStep";
import ModalStockCadastrar from "./ModalStockCadastrar";
import ModalStockUpdate from "./ModalStockUpdate";
import ModalEnhancedImportStock from "./enhanced-import/ModalEnhancedImportStock";
import "./ModalAdicionarNoStock.scss";
import { useLoading } from "../../../shared/context/LoadingContext";
import { momentGuardado } from "../../../shared/util/datasGuardadas";
import moment from "moment";
import NotaInformativa from "../../../shared/components/notaInformativa/NotaInformativa";

function ModalAdicionarNoStock({
  open,
  handleCloseModalAdicionarStock,
  atualizarStock,
  sendRequest,
  clinicaId,
  auth,
  stock,
  categoriasStock,
  produtoInicial,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [show, setShow] = useState(produtoInicial ? "entrada" : "firstStep");
  const [produtos, setProdutos] = useState([]);

  useEffect(() => {
    const fetchProdutos = async () => {
      startLoading();
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/produtos/clinica/${clinicaId}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );

        if (responseData.produtos) setProdutos(responseData.produtos);
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchProdutos();
  }, [sendRequest, clinicaId, auth.token]);

  const handleSubmitEntradaStock = async (dados) => {
    startLoading();

    const produtos = dados.map((prod) => ({
      nome: prod.nome,
      categoriaStock: prod.categoriaStock,
      quantidade: prod.quantidade,
      quantidadeIdeal: prod.quantidadeIdeal || 2,
      lote: prod.lote || "N/A", // O backend vai gerar lote automático se for N/A
      validade: prod.validade ? prod.validade.toISOString() : null,
      unidadeMedida: prod.unidadeMedida,
      classificacao: prod.classificacao || "PA",
      precoCompra: prod.precoCompra || 0,
      fornecedor: prod.fornecedor || null,
      localizacao: prod.localizacao || null,
    }));

    try {
      const formData = new FormData();
      formData.append("clinica", clinicaId);
      formData.append("produtos", JSON.stringify(produtos));
      formData.append("responsavelMovimento", auth.userId);
      formData.append("criadoPor", auth.userId);

      const stockReturned = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/adicionarnostock`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      atualizarStock(stockReturned.stock, "Produto(s) adicionado(s) ao stock");
    } catch (err) {
      console.error("err", err);
    } finally {
      handleCloseModalAdicionarStock();
      stopLoading();
    }
  };

  const handleSubmitUpdateStock = async (produtosSelecionados) => {
    startLoading();
    try {
      // Filtrar apenas produtos com quantidades adicionadas
      const produtosComAdicao = produtosSelecionados.filter((item) =>
        item.lotes.some((lote) => lote.quantidadeAdicionada > 0),
      );

      if (produtosComAdicao.length === 0) {
        console.error("Nenhum produto selecionado para adicionar");
        return;
      }

      // Preparar os dados de produtos para criar novos lotes
      const updatedData = produtosComAdicao.map((item) => {
        // Extrair os lotes que receberam adições
        const lotesComAdicao = item.lotes.filter(
          (lote) => lote.quantidadeAdicionada > 0,
        );

        // Criar novos lotes
        const novosLotes = lotesComAdicao.map((lote) => {
          // Determinar o código do lote
          let codigoLote;
          if (!lote.isTemporaryLot && lote.novoLote === "") {
            // É um lote existente sem alteração no nome
            codigoLote = lote.lote;
          } else if (lote.novoLote && lote.novoLote !== "") {
            // Foi especificado um novo código de lote
            codigoLote = lote.novoLote;
          } else {
            // Deixar como N/A para auto-gerar
            codigoLote = "N/A";
          }

          return {
            quantidade: lote.quantidadeAdicionada,
            // A validade vem do servidor (não há picker neste modal), por isso
            // lê-se em UTC — senão cada "dar entrada" recuava-a um dia.
            dataValidade: lote.dataValidade
              ? momentGuardado(lote.dataValidade).format("YYYY-MM-DD")
              : null,
            lote: codigoLote,
            precoCompra: lote.novoPrecoCompra || 0,
            fornecedor: lote.novoFornecedor || null,
            localizacao: lote.localizacao || null,
            autoGerado: codigoLote === "N/A",
            // NOVO: Flag para forçar atualização de metadados
            forcarAtualizacao: !lote.isTemporaryLot && lote.novoLote === "",
          };
        });

        return {
          produto: item.produto._id,
          lotes: novosLotes,
          quantidadeTotal:
            item.quantidadeTotal +
            lotesComAdicao.reduce(
              (total, lote) => total + lote.quantidadeAdicionada,
              0,
            ),
          quantidadeIdeal: item.quantidadeIdeal,
        };
      });

      // Preparar os movimentos para o histórico
      const historicoMovimentos = produtosComAdicao.flatMap((item) =>
        item.lotes
          .filter((lote) => lote.quantidadeAdicionada > 0)
          .map((lote) => {
            let codigoLote;
            if (!lote.isTemporaryLot && lote.novoLote === "") {
              codigoLote = lote.lote;
            } else if (lote.novoLote && lote.novoLote !== "") {
              codigoLote = lote.novoLote;
            } else {
              codigoLote = "N/A";
            }

            return {
              clinica: clinicaId,
              produto: item.produto._id,
              lote: codigoLote,
              dataValidade: lote.dataValidade
                ? momentGuardado(lote.dataValidade).format("YYYY-MM-DD")
                : null,
              responsavelMovimento: { tipo: "User", responsavel: auth.userId },
              quantidade: lote.quantidadeAdicionada,
              data: new Date(),
              tipoMovimento: "entrada",
              precoCompra: lote.novoPrecoCompra || 0,
              fornecedor: lote.novoFornecedor || null,
              localizacaoDestino: lote.localizacao || null,
            };
          }),
      );

      // Enviar os dados para o backend
      const formData = new FormData();
      formData.append("produtosAtualizados", JSON.stringify(updatedData));
      formData.append(
        "historicoMovimentos",
        JSON.stringify(historicoMovimentos),
      );

      const stockReturned = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/updateStockPartial/${stock.id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      atualizarStock(
        stockReturned.stock,
        "Novos lotes adicionados com sucesso",
      );
    } catch (err) {
      console.error("Erro ao adicionar novos lotes:", err);
    } finally {
      stopLoading();
      handleCloseModalAdicionarStock();
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseModalAdicionarStock}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          className="fluxo-atendimento__modal__box adicionarNoStock__modal"
          id={
            show === "porCadastrar"
              ? "modal__adicionarNoStock__modal"
              : "modal__adicionarNoStockGeral"
          }
        >
          <h1 className="fluxo-atendimento__modal__titulo">
            {show === "firstStep" && "Escolha a operação"}
            {show === "cadastrado" && "Alterando produto no stock"}
            {show === "porCadastrar" && "Entrada no stock"}
            {show === "import" && "Importar xls"}
            {show === "entrada" && "Dar entrada no produto"}
          </h1>

          {/* Só nos passos em que se dá entrada num produto já existente. No
              primeiro ecrã e na importação de ficheiro isto não se aplica, e
              uma nota que aparece onde não interessa deixa de ser lida onde
              interessa. */}
          {(show === "cadastrado" || show === "entrada") && (
            <NotaInformativa
              titulo="Como é tratado o número do lote"
              style={{ margin: "0 0 18px" }}
            >
              <ul style={{ margin: 0, paddingLeft: "18px" }}>
                <li>
                  Se escrever um número de lote que <strong>já existe</strong>{" "}
                  neste produto, a quantidade é <strong>somada a esse lote</strong>
                  , em vez de se criar outro. A validade, o preço de compra e o
                  fornecedor são atualizados com o que preencher — o que deixar
                  em branco fica como estava. Se indicar uma localização
                  diferente, o lote passa a estar nas duas.
                </li>
                <li>
                  Se o número for <strong>novo</strong>, é criado um lote novo,
                  com os seus próprios dados.
                </li>
                <li>
                  Um lote que estava <strong>esgotado</strong> volta a ficar
                  ativo ao receber unidades — não se cria um duplicado com o
                  mesmo número.
                </li>
                <li>
                  Deixar o número em branco gera um lote automático, sempre
                  novo.
                </li>
              </ul>
            </NotaInformativa>
          )}
          {show === "firstStep" && (
            <ModalStockFirstStep
              tipoEntradaHandler={(tipo) => setShow(tipo)}
              produtos={produtos}
            />
          )}
          {show === "porCadastrar" && (
            <ModalStockCadastrar
              handleCloseModalAdicionarStock={handleCloseModalAdicionarStock}
              handleSubmitEntradaStock={handleSubmitEntradaStock}
              sendRequest={sendRequest}
              categoriasStock={categoriasStock}
              clinicaId={clinicaId}
              auth={auth}
            />
          )}
          {show === "cadastrado" && (
            <ModalStockUpdate
              clinicaId={clinicaId}
              stock={stock}
              handleCloseModalAdicionarStock={handleCloseModalAdicionarStock}
              handleSubmitUpdateStock={handleSubmitUpdateStock}
              categoriasStock={categoriasStock}
              auth={auth}
              sendRequest={sendRequest}
            />
          )}

          {show === "entrada" && (
            <ModalStockUpdate
              clinicaId={clinicaId}
              stock={stock}
              handleCloseModalAdicionarStock={handleCloseModalAdicionarStock}
              handleSubmitUpdateStock={handleSubmitUpdateStock}
              categoriasStock={categoriasStock}
              auth={auth}
              sendRequest={sendRequest}
              produtoInicial={produtoInicial}
            />
          )}

          {show === "import" && (
            <ModalEnhancedImportStock
              stock={stock}
              handleCloseModal={handleCloseModalAdicionarStock}
              sendRequest={sendRequest}
              produtos={produtos}
              atualizarStock={atualizarStock}
              categoriasStock={categoriasStock}
              open={show === "import"}
            />
          )}
        </Box>
      </Modal>
    </>
  );
}

export default ModalAdicionarNoStock;
